import {
  checkIfAgencyIdIsAllowedForFamilyAndFriends,
  countDaysUntil,
  getActiveSession,
  getCanvasItineraryName,
  getDaysToDisableLightLoginOrHidePrepaid,
  getIsoDatePart,
  getLanguageMapping,
  GetShopExcursionMyMSC,
  GetShopOBSMyMSC,
  GetVoyagerClubInfoMyMSC,
} from '@msc-b2c/shared-utils';
import {
  CabinI,
  GetBookingMyMscResponse,
  IGetExperienceDetailsResponse,
  MyMscExcursion,
  PassengerI,
  PlusgradeResponse,
} from '@msc-b2c/interfaces/interfaces';
import {
  BookingChannelEnum,
  BookingPaymentMethodEnum,
  BookingStatusEnum,
  PriceBasisEnum,
} from '@msc-b2c/interfaces/enums';
import { CategoryFilter, OBSObjI, PaymentType } from '@msc-b2c/interfaces/types';

const EXPERIENCE_BENEFITS_DESCRIPTION_REGEX = /^\s*(.+?)\s+experience benefits\s*$/i;
export const CHECKOUT_EMAIL_VALIDATION_EVENT = 'msc:validate-checkout-email';

export type CheckoutEmailValidationEvent = CustomEvent<{ respond: (isValid: boolean) => void }>;

const getShopRequestContext = () => {
  const { cruiseId = '', bookingNumber = '' } = getActiveSession();

  return {
    cruiseId,
    bookingNumber: String(bookingNumber),
  };
};

const toIsoDateQueryParam = (value?: string): string =>
  value?.trim() ? getIsoDatePart(value) : '';

export const buildExcursionShopRequestKey = (card: MyMscExcursion): string => {
  const { cruiseId, bookingNumber } = getShopRequestContext();

  return [
    'excursion',
    bookingNumber,
    cruiseId,
    card.code ?? '',
    card.locationCode ?? '',
    toIsoDateQueryParam(card.startDate),
    toIsoDateQueryParam(card.endDate),
    card.priceBasis ?? '',
  ].join('|');
};

export const buildObsShopRequestKey = (card: OBSObjI): string => {
  const { cruiseId, bookingNumber } = getShopRequestContext();

  return [
    'obs',
    bookingNumber,
    cruiseId,
    card.code ?? '',
    card.deliveryDate ?? '',
    card.type?.code ?? '',
    card.priceBasis ?? '',
  ].join('|');
};

const getFirstValidRelatedObsCode = (card: OBSObjI, existingCodes: string[]): string => {
  const relatedObsCodes = [
    ...(card.prepaidItems?.serviceCodes?.adults ?? []),
    ...(card.prepaidItems?.serviceCodes?.minors ?? []),
  ];

  return relatedObsCodes.find((code) => existingCodes.includes(code)) ?? '';
};

/**
 * Checks if the current date is within a certain number of days before the departure date.
 *
 * @param {string} departureDate - The departure date to compare against.
 * @returns {boolean} True if the current date is within the specified number of days before the departure date, false otherwise.
 */
export const isXDaysBeforeDeparture = (departureDate: string): boolean => {
  if (departureDate) {
    return (
      countDaysUntil(undefined, departureDate) <= (getDaysToDisableLightLoginOrHidePrepaid() ?? 0)
    );
  }
  return false;
};

/**
 * Retrieves the cabin object of the logged-in user based on the booking information.
 *
 * @param {GetBookingMyMscResponse} booking - The booking information containing cabin details.
 * @returns {CabinI | undefined} - The cabin object assigned to the logged-in user, or undefined if not found.
 */
export const getCabinObjectOfTheLoggedUser = (
  booking: GetBookingMyMscResponse | undefined
): CabinI | undefined => {
  const cabinIDOfTheLoggedUser = getCabinIdOfTheLoggedUser(booking);
  return cabinOfTheLoggedUser(booking?.cabins, cabinIDOfTheLoggedUser);
};

/**
 * Retrieves the departure date for the logged-in user's cabin. If the logged-in user's cabin is not found,
 * it falls back to the general departure date from the booking information.
 *
 * @param {GetBookingMyMscResponse} booking - The booking information containing cabin and cruise details.
 * @returns {string} - The departure date of the logged-in user's cabin, or the general booking departure date if not found.
 */
export const getDepartureDate = (booking: GetBookingMyMscResponse | undefined): string => {
  if (booking) {
    const cabinLoggedUser: CabinI | undefined = getCabinObjectOfTheLoggedUser(booking);
    return cabinLoggedUser?.cruise?.departureDate ?? booking?.departureDate;
  }
  return '';
};

/**
 * Retrieves the booking status for the logged-in user's cabin. If the logged-in user's cabin is not found,
 * it falls back to the general booking status from the booking information.
 *
 * @param {GetBookingMyMscResponse} booking - The booking information containing cabin and booking status details.
 * @returns {string} - The booking status of the logged-in user's cabin, or the general booking status if not found.
 */
export const getBookingStatus = (booking: GetBookingMyMscResponse): string => {
  const cabinLoggedUser: CabinI | undefined = getCabinObjectOfTheLoggedUser(booking);
  return cabinLoggedUser?.bookingStatus?.code ?? booking?.status?.code;
};

/**
 * Checks if payment is enabled for a given booking. Payment is enabled if:
 *    1. The booking status is 'BOOKED' or 'OPTIONAL'.
 *    2. The booking channel is 'B2C' or the payment method is 'DIRECT'.
 *    3. The booking payment type is 'DEPOSIT' or 'TOTALTRAVEL'.
 *    4. The gross balance due without prepaid is greater than 0 or the gross balance due is greater than 0.
 *
 * @param {GetBookingMyMscResponse} booking - The booking information.
 * @returns {boolean} True if payment is enabled, false otherwise.
 */
export const isPaymentEnabled = (booking: GetBookingMyMscResponse | undefined): boolean => {
  if (booking) {
    const isAgencyIdAllowedForFamilyAndFriends = checkIfAgencyIdIsAllowedForFamilyAndFriends(
      booking?.agency.id
    );
    const grossBalanceDue = booking?.total?.due?.gross;
    const grossBalanceDueNonPrePaid = booking?.totalWithoutPrepaid?.due?.gross;

    const bookingPaymentType: string =
      grossBalanceDue && Number(grossBalanceDue) > 0
        ? PaymentType.DEPOSIT
        : PaymentType.TOTALTRAVEL;
    return (
      (booking.status.code === BookingStatusEnum.BOOKED ||
        booking.status.code === BookingStatusEnum.OPTIONAL) &&
      isB2CBooking(booking) &&
      (bookingPaymentType === PaymentType.DEPOSIT ||
        bookingPaymentType === PaymentType.TOTALTRAVEL) &&
      ((grossBalanceDueNonPrePaid && Number(grossBalanceDueNonPrePaid) > 0) ||
        Number(grossBalanceDue) > 0) &&
      isAgencyIdAllowedForFamilyAndFriends
    );
  }
  return false;
};

export const isPlusgradeEligible = (plusgrade: PlusgradeResponse | undefined): boolean => {
  return plusgrade?.isEligible ?? false;
};

/**
 * Builds dynamic category filters based on the provided excursions and category filters.
 *
 * @param {Array} excursions - An array of excursion objects.
 * @param {Array} categoryFilters - An array of category filter objects.
 * @returns {Array} - An array of filtered category filters.
 */
export function buildDynamicCategoryFilters(
  excursions: MyMscExcursion[] | undefined,
  categoryFilters: CategoryFilter[] | undefined
) {
  return !excursions || excursions.length === 0
    ? []
    : categoryFilters?.filter((filter: CategoryFilter) =>
        excursions?.some(
          (excursion: MyMscExcursion) => excursion?.activityGroup?.details === filter.filterType
        )
      );
}

export function getItineraryNameByCanvas(demographicArea: string): string {
  const canvasList = getCanvasItineraryName();
  for (const part of demographicArea.split('|')) {
    const name = canvasList.find((canvas) => canvas?.code === part)?.name;
    if (name) return name;
  }
  return '';
}

/**
 * Retrieves the cabin ID of the logged-in user based on the active session.
 *
 * @param {GetBookingMyMscResponse} booking - The booking info from getBooking API.
 * @returns {string | undefined} - The ID of the cabin the logged-in user is assigned to, or undefined if not found.
 */
export function getCabinIdOfTheLoggedUser(
  booking: GetBookingMyMscResponse | undefined
): string | undefined {
  const loggedUserId = getActiveSession().passengerId;
  const cruiseId = getActiveSession().cruiseId;
  const cabin = booking?.cabins?.find((cabin) =>
    cabin.passengers?.some(
      (passenger) => passenger.id === loggedUserId && cruiseId === cabin.cruise.id
    )
  );
  return cabin ? cabin.id : undefined;
}

/**
 * Checks if a specific passenger ID is present in a given cabin of the booking.
 *
 * @param {GetBookingMyMscResponse} booking - The booking info from getBooking API.
 * @param {string} cabinId - The ID of the cabin to check.
 * @param {string} passengerId - The ID of the passenger to look for.
 * @returns {boolean} - True if the passenger ID is found in the specified cabin, false otherwise.
 */
export function isPassengerIdInLoggedUserCabin(
  booking: GetBookingMyMscResponse,
  cabinId: string,
  passengerId: string
): boolean {
  const cabin = booking.cabins.find((cabin) => cabin.id === cabinId);
  return cabin?.passengers?.some((passenger) => passenger.id === passengerId) || false;
}

/**
 * Finds a cabin by its ID from a list of cabins.
 *
 * @param {GetBookingMyMscResponse['cabins']} cabins - An array of cabin objects from the booking response.
 * @param {string} cabinId - The ID of the cabin to find.
 * @returns {GetBookingMyMscResponse['cabins'][i] | undefined} - The cabin object with the specified ID (used for the logged user cabin), or undefined if not found.
 */
export function cabinOfTheLoggedUser(cabins?: CabinI[], cabinId?: string): CabinI | undefined {
  return cabinId && cabins && cabins?.length > 0
    ? cabins?.find((cabin: CabinI) => cabin?.id === cabinId)
    : undefined;
}

/**
 * Retrieves the language label corresponding to a given language code.
 *
 * @param {string} languageCode - The code of the language to look up.
 * @returns {string} - The label of the language if the code exists in the mapping, or an empty string if not found.
 */
export function getLanguageLabelFromCode(languageCode: string | undefined): string {
  // get context aware language mapping
  const languageMapping = getLanguageMapping();
  // find the language label for the given language code
  const language = languageCode
    ? languageMapping?.find((lang) => lang.languageCode === languageCode)
    : undefined;
  return language ? language.languageLabel : languageCode ?? '';
}

/**
 * Returns true when the API response is an error body (e.g. 400 bad_request)
 * rather than a valid shop excursion payload.
 * The `errorId` field is unique to Mulesoft error responses.
 */
export function isShopApiErrorResponse(res: unknown): boolean {
  return !!res && typeof res === 'object' && 'errorId' in res;
}

/**
 Retrieves shop excursion data based on the provided card details.

 @param {object} card - The card object containing excursion details.
 @param {string} card.code - The code of the excursion.
 @param {string} card.locationCode - The location code of the excursion.
 @param {string} card.startDate - The start date of the excursion.
 @param {string} card.endDate - The end date of the excursion.
 @param {string} card.priceBasis - The price basis of the excursion.
 @returns - The data of the shop excursion.
 @throws When the API returns an error response instead of valid excursion data.
 */
export async function getShopExcursionData(card: MyMscExcursion) {
  const session = getActiveSession();

  const result = await new GetShopExcursionMyMSC(false, {
    cruiseId: session.cruiseId ?? '',
    excursionCode: card.code ?? '',
    bookingNumber: Number(session.bookingNumber),
    locationCode: card.locationCode ?? '',
    startDate: toIsoDateQueryParam(card.startDate),
    endDate: toIsoDateQueryParam(card.endDate),
    priceBasis: card.priceBasis ?? PriceBasisEnum.PER_PERSON,
  }).getData();

  if (isShopApiErrorResponse(result)) {
    throw result;
  }

  return result;
}

export function getShopOBSData(card: OBSObjI) {
  const session = getActiveSession();

  const supportedPriceBases = Object.values(PriceBasisEnum) as string[];
  const resolvedPriceBasis = supportedPriceBases.includes(card.priceBasis ?? '')
    ? (card.priceBasis as PriceBasisEnum)
    : PriceBasisEnum.PER_PERSON;

  return new GetShopOBSMyMSC(false, {
    cruiseId: session.cruiseId ?? '',
    obsCode: card.code ?? '',
    bookingNumber: Number(session.bookingNumber),
    deliveryDate: card.deliveryDate ?? '',
    type: card.type.code ?? '',
    priceBasis: resolvedPriceBasis,
  }).getData();
}

export function getMembershipData() {
  return new GetVoyagerClubInfoMyMSC(false, {
    email: getActiveSession().email || '',
  }).getData();
}

export async function getAllShopOBSData(card: OBSObjI, existingCodes: string[]) {
  const firstValidRelatedCode = getFirstValidRelatedObsCode(card, existingCodes);
  const codesToFetch = Array.from(
    new Set([card.code, firstValidRelatedCode].filter((code): code is string => Boolean(code)))
  );

  const shopPromises = codesToFetch.map((obsCode) =>
    getShopOBSData({
      ...card,
      code: obsCode,
    } as OBSObjI)
  );
  const allShopData = await Promise.allSettled(shopPromises);
  return allShopData.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
}

/**
 Retrieves the age categories of passengers based on their IDs from the booking information.

 @param {string[]} passengerId - An array of passenger IDs to look up.
 @param {GetBookingMyMscResponse} booking - The booking information containing passenger details.
 @returns {string[]} - An array of age categories for the specified passengers, or an empty array if no matching passengers are found.
 */
export function getCategoryAgeOfPassengerIds(
  passengerId: string[],
  booking: GetBookingMyMscResponse
): string[] {
  if (!passengerId.length || !booking.passengers?.length) {
    return [];
  } else {
    // return the ageCategory of passengers who have already booked the prepaid
    return booking.passengers
      .filter((passenger: PassengerI) => passengerId.includes(passenger.id))
      .map((passenger: PassengerI) => passenger.ageCategory);
  }
}

/**
 * Calculates the number of adults in a cabin based on the provided passenger list and booking information.
 *
 * @param {Array<{ id?: string; number?: number }>} passengers - An array of passenger objects, each containing an ID and number.
 * @param {GetBookingMyMscResponse} booking - The booking information containing passenger details.
 * @returns {number} - The count of passengers categorized as 'SENIOR' or 'ADULT'.
 */
export function getNumberOfAdultsInCabin(
  passengers:
    | {
        id?: string;
        number?: number;
      }[]
    | undefined,
  booking: GetBookingMyMscResponse
): number {
  if (!passengers || passengers.length === 0) {
    return 0;
  } else {
    // get age category of each passenger using getCategoryAgeOfPassengerIds
    const ageCategories = getCategoryAgeOfPassengerIds(
      passengers.map((passenger) => passenger.id ?? ''),
      booking
    );
    // count number of SENIOR and ADULTS
    return ageCategories.filter(
      (ageCategory) => ageCategory === 'SENIOR' || ageCategory === 'ADULT'
    ).length;
  }
}

/**
 * Extracts the ship code from a given cruise ID.
 * @param cruiseId
 * @returns {string} - The ship code extracted from the cruise ID.
 */
export function getShipCodeFromCruiseIdPA(cruiseId: string | undefined) {
  return cruiseId?.substring(0, 2) ?? ''; // Get the first two letters
}

/***** Routing Event Listener - Start ****/

// Name of the event
export const ROUTE_CHANGED = 'routeChanged';

/***** Routing Event Listener - End ****/

/**
 * Decodes HTML entities in a given string.
 *
 * @param {string} text - The input string containing HTML entities to decode.
 * @returns {string} - The decoded string with HTML entities converted to their corresponding characters.
 */
export function decodeHTMLEntities(text: string | undefined): string {
  if (!text) {
    return '';
  }
  const txt = document.createElement('textarea');
  txt.innerHTML = text;
  return txt.value;
}

/**
 * Determines if a booking is a B2C (Business-to-Consumer) booking.
 *
 * A booking is considered B2C if:
 * 1. The booking channel is `B2C`.
 * 2. The agency associated with the booking is marked as a B2C agency.
 * 3. The payment method for the booking is `DIRECT`.
 *
 * @param {GetBookingMyMscResponse} bookingData - The booking data to evaluate.
 * @returns {boolean} - Returns `true` if the booking is a B2C booking, otherwise `false`.
 */
export function isB2CBooking(bookingData: GetBookingMyMscResponse): boolean {
  return (
    bookingData?.bookingChannel === BookingChannelEnum.B2C ||
    bookingData?.agency?.isB2CAgency ||
    bookingData?.paymentMethod === BookingPaymentMethodEnum.DIRECT
  );
}

/**
 * Retrieves the additional items of the logged-in user from the booking information.
 *
 * @param {GetBookingMyMscResponse | undefined} booking - The booking information containing additional items.
 * @returns {string[]} - An array of codes representing the additional items of the logged-in user, or an empty array if no additional items are found.
 */
export function getLoggedUserAdditionalItemsOfBooking(
  booking: GetBookingMyMscResponse | undefined
): string[] {
  const { passengerId } = getActiveSession();
  if (!Array.isArray(booking?.onboardServices)) return [];
  const loggedUserCabin = getCabinIdOfTheLoggedUser(booking);
  // Return the additional items codes of the logged-in user
  return (
    booking?.onboardServices
      ?.filter((item) =>
        item.passengers?.some(
          (passenger) => passenger.id === passengerId && loggedUserCabin === item.relatedItem?.id
        )
      )
      .map((item) => item.code) ?? []
  );
}

/**
 * Checks if there is a matching code between two lists of codes.
 *
 * @param {string[]} experienceItemCodeList - A list of experience item codes to check against.
 * @param {string[]} additionalItemsCode - A list of additional item codes to look for matches.
 * @returns {boolean} - Returns `true` if any code in `additionalItemsCode` exists in `experienceItemCodeList`, otherwise `false`.
 */
export function hasMatchingCode(
  experienceItemCodeList: string[],
  additionalItemsCode: string[]
): boolean {
  return additionalItemsCode?.some((code) => experienceItemCodeList?.includes(code));
}

/**
 * Retrieves the booked experience detail from the provided booking data and experiences.
 *
 * @param {GetBookingMyMscResponse} bookingData - The booking data containing purchased items.
 * @param {IGetExperienceDetailsResponse} experiences - The response containing experience details.
 * @returns {object | undefined} - Returns the matching experience object if found, otherwise `undefined`.
 */
export function getBookedExperienceDetail(
  bookingData: GetBookingMyMscResponse,
  experiences?: IGetExperienceDetailsResponse
): object | undefined {
  // Retrieve purchased items in the booking
  const additionalItemsCode = getLoggedUserAdditionalItemsOfBooking(bookingData);

  // Find in each experienceCodeList if some additionalItemCode exists, then return that experience
  return experiences?.data?.experiences?.items?.find(({ experienceItemCodeList }) =>
    hasMatchingCode(experienceItemCodeList, additionalItemsCode)
  );
}

/**
 * Extracts an experience title from the first onboard service description that matches
 * `EXPERIENCE_BENEFITS_DESCRIPTION_REGEX`.
 *
 * @param bookingData - Booking data that may contain onboard service descriptions.
 * @returns The trimmed experience title when a matching description is found, otherwise `undefined`.
 */
export function getExperienceTitleFallback(
  bookingData?: GetBookingMyMscResponse
): string | undefined {
  for (const { description } of bookingData?.onboardServices ?? []) {
    const match = EXPERIENCE_BENEFITS_DESCRIPTION_REGEX.exec(description ?? '');

    if (match?.[1]) {
      return match[1].trim();
    }
  }

  return undefined;
}

/**
 * Returns the category (INS, OUT, BAL, SUI, YTC) of a given cabin code.
 *
 * @param cabinCode - The cabin code to search for.
 * @param mappedCategories - The result of mapCabinCategories.
 * @returns The category code (INS, OUT, BAL, SUI, YTC) or undefined if not found.
 */
export function findCabinCategory(
  cabinCode: string | undefined,
  mappedCategories: Record<string, string[]>
): string | undefined {
  for (const [category, codes] of Object.entries(mappedCategories)) {
    if (cabinCode && codes?.includes(cabinCode)) {
      return category;
    }
  }
  return undefined;
}
